import type {
  ReportDetailGovernance, ReportDetailOperations, ReportDetailOverview, ReportDetailScheduling, ReportDetailUsage,
} from '../types/reportDetail'
import type { ReportDetailWindow } from '../utils/reportDetailPath'
import { fetchApi } from './api'

/**
 * Report Detail's five reads — ObservatoryReportDetailController, LighthouseAdmin at
 * class level, GET only (D6a: Observatory is read-only over Cartographer data).
 *
 * Through the shared `fetchApi`, so a failure arrives as an ApiError carrying its
 * status and each tab can tell a 403 (restricted) from a 404 (no such report) from
 * a 400 (a window not offered) from a 500.
 *
 * The other two tabs have no read here: Access is `services/reportAccessApi.ts`
 * and History is `services/auditLogApi.ts` with `entityId`. The Governance tab's
 * episodes panel is `services/retirementEpisodesApi.ts` with `reportId`.
 *
 * The id is encoded: it comes from the URL, and a path is not the place to trust
 * it. A malformed one does not match the server's `:guid` constraint and is a 404.
 */

function base(reportId: string): string {
  return `/api/observatory/registry/${encodeURIComponent(reportId)}`
}

/** GET {reportId} — the report, where it lives, whose it is, and what reaches it. */
export function getReportDetailOverview(reportId: string): Promise<ReportDetailOverview> {
  return fetchApi<ReportDetailOverview>(base(reportId))
}

/** GET {reportId}/governance — ownership and its history, attention, archive and retirement. */
export function getReportDetailGovernance(reportId: string): Promise<ReportDetailGovernance> {
  return fetchApi<ReportDetailGovernance>(`${base(reportId)}/governance`)
}

/**
 * GET {reportId}/operations?days= — what the run log recorded, by source.
 *
 * `days` is always sent, the default included, so the URL a cache entry was
 * fetched from always names its window.
 */
export function getReportDetailOperations(reportId: string, days: ReportDetailWindow): Promise<ReportDetailOperations> {
  return fetchApi<ReportDetailOperations>(`${base(reportId)}/operations?days=${days}`)
}

/** GET {reportId}/usage?days= — who ran it and how often: runs recorded, not views. */
export function getReportDetailUsage(reportId: string, days: ReportDetailWindow): Promise<ReportDetailUsage> {
  return fetchApi<ReportDetailUsage>(`${base(reportId)}/usage?days=${days}`)
}

/** GET {reportId}/scheduling — every schedule, its last outcome and its next firings. */
export function getReportDetailScheduling(reportId: string): Promise<ReportDetailScheduling> {
  return fetchApi<ReportDetailScheduling>(`${base(reportId)}/scheduling`)
}
